import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, output } from '@angular/core';
import { DxPopoverModule, DxSelectBoxModule } from 'devextreme-angular';
import type { DashboardRevenueProjectionMethodDto } from 'ag-common-lib/lib/contracts/reporting-api';
import { isAimingAt, Point } from 'ag-common-svc/lib/shared/utils/pointer-safe-triangle';
import { PROJECTION_METHOD_OPTIONS, type ProjectionMethodOption } from './projection-method-options';
import { ProjectionMethodHelpComponent } from './projection-method-help.component';

@Component({
  selector: 'ag-shr-projection-method-select',
  standalone: true,
  imports: [CommonModule, DxSelectBoxModule, DxPopoverModule, ProjectionMethodHelpComponent],
  templateUrl: './projection-method-select.component.html',
  styleUrls: ['./projection-method-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'projection-method-select' },
})
export class ProjectionMethodSelectComponent {
  private changeDetectorRef = inject(ChangeDetectorRef);

  readonly value = input.required<DashboardRevenueProjectionMethodDto>();
  readonly disabled = input(false);
  readonly valueChange = output<DashboardRevenueProjectionMethodDto>();

  protected readonly methodOptions = PROJECTION_METHOD_OPTIONS;
  /**
   * The list needs more room than the input: the closed box only shows a one-word label, but
   * each option carries a tagline that reads as a sentence. Held as a field, not an inline
   * object literal — a new reference each change detection makes dx re-create the popup.
   */
  protected readonly methodDropDownOptions = { width: 300 };

  /**
   * The method picker gets its own popover rather than sharing another one: it opens
   * from inside the dxSelectBox's dropdown overlay, which sits above the host and outlives
   * a row hover — one shared instance would be retargeted out from under an open list.
   */
  protected isMethodPopoverVisible = false;
  protected methodPopoverTarget: EventTarget | Element | null = null;
  protected methodPopoverOption: ProjectionMethodOption | null = null;

  /**
   * Closing is deferred so the pointer can cross the gap between the list item and the
   * popover without dismissing it. Without this the popover is unreadable whenever its
   * content is taller than the box: moving toward it to scroll is what kills it.
   */
  private methodPopoverCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Safety-triangle state. Reaching the popover means moving diagonally across the *other*
   * options, and each one they cross would otherwise steal the popover — so while the pointer
   * is aiming at the open popover, hovers over intervening items are held rather than applied.
   * `AIM_GRACE_MS` is the ceiling on that hold: pause inside the corridor and the item you
   * actually stopped on wins, so aiming can never wedge the picker.
   */
  private static readonly AIM_GRACE_MS = 350;
  private previousPointer: Point | null = null;
  private pendingMethodOption: ProjectionMethodOption | null = null;
  private pendingMethodTarget: EventTarget | Element | null = null;
  private methodAimTimer: ReturnType<typeof setTimeout> | null = null;

  protected onValueChanged = (event: { value?: DashboardRevenueProjectionMethodDto }): void => {
    const nextValue = event?.value;
    if (!nextValue || nextValue === this.value()) {
      return; // dxSelectBox also fires on programmatic writes; don't echo our own write
    }
    this.valueChange.emit(nextValue);
  };

  private cancelMethodPopoverClose = (): void => {
    if (this.methodPopoverCloseTimer !== null) {
      clearTimeout(this.methodPopoverCloseTimer);
      this.methodPopoverCloseTimer = null;
    }
  };

  private cancelMethodAim = (): void => {
    if (this.methodAimTimer !== null) {
      clearTimeout(this.methodAimTimer);
      this.methodAimTimer = null;
    }
    this.pendingMethodOption = null;
    this.pendingMethodTarget = null;
  };

  /** Live rect: the popover moves as it retargets, so a cached one would aim at a ghost. */
  private methodPopoverRect = (): DOMRect | null =>
    document.querySelector('.projection-method-select__popover-host')?.getBoundingClientRect() ?? null;

  /** Feeds the triangle its apex — the point the pointer is coming *from*. */
  protected onMethodItemPointerMove = (event: MouseEvent): void => {
    this.previousPointer = { x: event.clientX, y: event.clientY };
  };

  private showMethodPopover = (target: EventTarget | Element | null, option: ProjectionMethodOption): void => {
    this.cancelMethodPopoverClose();
    this.methodPopoverOption = option;
    this.methodPopoverTarget = target;
    this.isMethodPopoverVisible = true;
  };

  protected openMethodPopover = (event: MouseEvent, option: ProjectionMethodOption): void => {
    const target = event.currentTarget ?? event.target;

    // The item we just left scheduled a close on its way out. The pointer is still inside the
    // list — whether it settles here or is only passing through toward the popover, that close
    // must not land, or the popover vanishes mid-flight.
    this.cancelMethodPopoverClose();

    // Nothing open yet, or re-entering the option already shown: no corridor to protect.
    if (!this.isMethodPopoverVisible || this.methodPopoverOption === option) {
      this.cancelMethodAim();
      this.showMethodPopover(target, option);
      return;
    }

    const rect = this.methodPopoverRect();
    const pointer = { x: event.clientX, y: event.clientY };

    if (rect && this.previousPointer && isAimingAt(this.previousPointer, pointer, rect)) {
      // Passing over this option en route to the popover — hold the switch, but let it land
      // if the pointer settles here instead of continuing.
      this.cancelMethodAim();
      this.pendingMethodOption = option;
      this.pendingMethodTarget = target;
      this.methodAimTimer = setTimeout(() => {
        const pending = this.pendingMethodOption;
        const pendingTarget = this.pendingMethodTarget;
        this.methodAimTimer = null;
        if (pending) {
          this.pendingMethodOption = null;
          this.pendingMethodTarget = null;
          this.showMethodPopover(pendingTarget, pending);
          this.changeDetectorRef.markForCheck(); // fired from a timer
        }
      }, ProjectionMethodSelectComponent.AIM_GRACE_MS);
      return;
    }

    this.cancelMethodAim();
    this.showMethodPopover(target, option);
  };

  /** Hovering the popover itself keeps it up — that is what makes its scroll reachable. */
  protected holdMethodPopover = (): void => {
    this.cancelMethodPopoverClose();
    this.cancelMethodAim(); // arrived: whatever was queued mid-flight is moot
  };

  protected closeMethodPopover = (): void => {
    this.cancelMethodPopoverClose();
    this.methodPopoverCloseTimer = setTimeout(() => {
      this.isMethodPopoverVisible = false;
      this.methodPopoverCloseTimer = null;
      this.cancelMethodAim();
      this.changeDetectorRef.markForCheck(); // fired from a timer, outside template bindings
    }, 250);
  };

  protected closeMethodPopoverNow = (): void => {
    this.cancelMethodPopoverClose();
    this.cancelMethodAim();
    this.isMethodPopoverVisible = false;
  };
}
